/**
 * Canonical-URL and indexing helpers for route `meta` functions.
 *
 * React Router's `meta` can emit `<link>` tags via `tagName: 'link'`, which is
 * how a canonical gets rendered — there's no separate `links` hook for it that
 * can see loader data.
 */

/** Minimal shape of the root match; `origin` comes from `getDomainUrl`. */
type MatchLike = { id: string; data?: unknown } | undefined

/**
 * The request origin as the root loader saw it (protocol + host, honouring
 * `X-Forwarded-*`). Returns undefined if root data is missing — e.g. when the
 * root loader itself errored — so callers can omit the tag rather than emit a
 * canonical pointing at the wrong host.
 */
export function originFromMatches(
	matches: ReadonlyArray<MatchLike>,
): string | undefined {
	const root = matches.find((m) => m?.id === 'root')
	const data = root?.data as { requestInfo?: { origin?: string } } | undefined
	return data?.requestInfo?.origin
}

/**
 * A canonical `<link>` for `path`, or nothing when the origin is unknown.
 * `path` must be root-relative and include its leading slash.
 */
export function canonicalLink(
	matches: ReadonlyArray<MatchLike>,
	path: string,
): Array<{ tagName: 'link'; rel: 'canonical'; href: string }> {
	const origin = originFromMatches(matches)
	if (!origin) return []
	return [{ tagName: 'link', rel: 'canonical', href: `${origin}${path}` }]
}

/**
 * Keep a page out of the index while still letting crawlers follow its links —
 * used by paid landing pages that duplicate an indexable service page. Pair it
 * with a `canonicalLink` to the page that should rank, and opt the route out of
 * the sitemap via `handle.getSitemapEntries`.
 */
export const NOINDEX_META = {
	name: 'robots',
	content: 'noindex, follow',
} as const
